import { Component, Input, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { SystemStatusService } from '@/app/core/service/system-status.service';

@Component({
    selector: 'app-pre-maintenance-banner',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './pre-maintenance-banner.component.html',
    styleUrls: ['./pre-maintenance-banner.component.scss']
})
export class PreMaintenanceBannerComponent implements OnInit, OnDestroy {
    
    @Input() position: 'fixed-top' | 'relative' | 'absolute-top' = 'relative';
    @Input() customMessage?: string;

    private readonly systemStatus = inject(SystemStatusService);

    countdownDisplay = '';
    countdownActive = false;
    private countdownTargetTs = 0;
    private countdownTimerId?: any;
    private statusSub?: Subscription;

    // Computed properties
    get isEnabled(): boolean {
        const s = this.systemStatus.maintenance;
        return s.preNotice.enabled && !s.enabled;
    }

    get preMaintenanceMsg(): string {
        const template =
            this.customMessage ||
            this.systemStatus.maintenance.preNotice.message ||
            'El sistema entrará en mantenimiento en {time}. Te recomendamos guardar tu trabajo.';
        return template.replace('{time}', this.countdownDisplay || '00:00');
    }

    private get scheduledAt(): number | null {
        return this.systemStatus.maintenance.preNotice.scheduledAt;
    }

    private get minutes(): number {
        return this.systemStatus.maintenance.preNotice.minutes || 30;
    }

    ngOnInit() {
        // Reaccionar a cambios del servidor
        this.statusSub = this.systemStatus.status$.subscribe(() => {
            this.syncWithServerStatus();
        });
        this.syncWithServerStatus();
    }

    ngOnDestroy() {
        this.statusSub?.unsubscribe();
        this.stopCountdown();
    }

    private syncWithServerStatus(): void {
        if (!this.isEnabled) {
            this.stopCountdown();
            return;
        }

        const ts = this.scheduledAt;
        if (!ts || Date.now() >= ts) {
            // Ya pasó o no hay timestamp: no mostrar
            this.stopCountdown();
            return;
        }

        // Si el target cambió (nueva activación), reiniciar countdown
        if (this.countdownTargetTs !== ts) {
            this.stopCountdown();
            this.countdownTargetTs = ts;
            this.countdownActive = true;
            this.tickCountdown();
            if (this.countdownTimerId) clearInterval(this.countdownTimerId);
            this.countdownTimerId = setInterval(() => this.tickCountdown(), 1000);
        }
    }

    private restoreCountdownFromStorage(): boolean { return false; }  // ya no se usa localStorage

    startCountdown(minutes: number) {
        const secs = Math.max(0, Math.round((minutes || 0) * 60));
        this.countdownTargetTs = Date.now() + secs * 1000;
        this.countdownActive = true;
        this.tickCountdown();
        if (this.countdownTimerId) clearInterval(this.countdownTimerId);
        this.countdownTimerId = setInterval(() => this.tickCountdown(), 1000);
    }

    stopCountdown(clearStorage: boolean = true): boolean {
        const hadActive = !!this.countdownTimerId || this.countdownActive;
        if (this.countdownTimerId) {
            clearInterval(this.countdownTimerId);
            this.countdownTimerId = undefined;
        }
        this.countdownActive = false;
        this.countdownDisplay = '';
        this.countdownTargetTs = 0;
        return hadActive;
    }

    private tickCountdown() {
        const remaining = Math.max(0, this.countdownTargetTs - Date.now());
        const totalSec = Math.ceil(remaining / 1000);
        const mm = Math.floor(totalSec / 60);
        const ss = totalSec % 60;
        this.countdownDisplay = `${mm.toString().padStart(2, '0')}:${ss.toString().padStart(2, '0')}`;
        
        if (remaining <= 0) {
            if (this.countdownTimerId) {
                clearInterval(this.countdownTimerId);
                this.countdownTimerId = undefined;
            }
            this.countdownDisplay = '00:00';
            this.countdownActive = false;
            this.countdownTargetTs = 0;
        }
    }
}